import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { MapPin, Plus, Edit, Trash2, ExternalLink, RefreshCw, Globe2 } from 'lucide-react';

// Admin CRUD for local SEO landing pages (one per city). Lets the admin
// review the auto-generated content, edit it inline, and toggle the
// publication flag. Pages stay in the database — they are NOT regenerated
// on each request, only when the admin explicitly clicks "Réinitialiser".
export const CityLandingPagesAdmin = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // page object or {new: true}

  const load = async () => {
    setLoading(true);
    try {
      const list = await api.adminListCityPages();
      setPages(list || []);
    } catch (err) {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublish = async (page) => {
    try {
      await api.adminUpdateCityPage(page.id, { is_published: !page.is_published });
      toast.success(page.is_published ? "Page dépubliée" : "Page publiée");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`Supprimer définitivement la page « ${page.city_name} » ?`)) return;
    try {
      await api.adminDeleteCityPage(page.id);
      toast.success("Page supprimée");
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("Créer les 10 pages villes par défaut (Marseille, Aix, Avignon, ...) ? Elles seront en brouillon.")) return;
    try {
      const res = await api.adminSeedCityPages();
      toast.success(`${res.seeded} nouvelles pages créées (${res.total} au total)`);
      load();
    } catch (err) {
      toast.error("Erreur de seed");
    }
  };

  return (
    <div className="space-y-6" data-testid="city-landing-pages-admin">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-1">SEO LOCAL</p>
          <h2 className="font-light text-2xl md:text-3xl text-[#1F2A4A]">
            Pages <span className="font-serif italic text-[#C9B896]">par ville.</span>
          </h2>
          <p className="text-sm text-[#1F2A4A]/65 mt-1 max-w-2xl">
            Une page d'atterrissage statique par ville cible — indexée par Google et accessible à <code className="text-xs bg-[#FAF7F0] px-1.5 py-0.5 rounded">/fr/sellier/&lt;slug&gt;</code>. Les pages restent en ligne tant qu'elles sont publiées.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleSeed}
            className="border-[#1F2A4A]/15 text-[#1F2A4A] hover:bg-[#FAF7F0] gap-2"
            data-testid="city-pages-seed-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Pré-remplir (10 villes)
          </Button>
          <Button
            onClick={() => setEditing({ _new: true, city_name: '', distance_km: 0, is_published: false })}
            className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] gap-2"
            data-testid="city-pages-add-btn"
          >
            <Plus className="w-4 h-4" />
            Nouvelle ville
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-12 text-center text-sm text-[#1F2A4A]/50">Chargement…</div>
      ) : pages.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-2xl border border-[#1F2A4A]/10">
          <Globe2 className="w-8 h-8 text-[#C9B896] mx-auto mb-3" />
          <p className="text-sm text-[#1F2A4A]/70 mb-4">Aucune page locale pour le moment.</p>
          <Button onClick={handleSeed} className="bg-[#C9B896] text-[#0F1623] hover:bg-[#D9C8A8] gap-2">
            <RefreshCw className="w-4 h-4" />
            Pré-remplir 10 villes
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {pages.map((p) => (
            <div
              key={p.id}
              className={`bg-white rounded-2xl border p-4 transition-all ${
                p.is_published ? 'border-[#C9B896]/40' : 'border-[#1F2A4A]/10 opacity-90'
              }`}
              data-testid={`city-page-card-${p.city_slug}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#C9B896]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1F2A4A] truncate">{p.city_name}</p>
                  <p className="text-xs text-[#1F2A4A]/55 truncate">
                    {p.distance_km} km · {p.region}
                  </p>
                  <p className="text-[10px] font-mono text-[#1F2A4A]/40 mt-1 truncate">
                    /{p.city_slug}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={p.is_published}
                    onCheckedChange={() => togglePublish(p)}
                    data-testid={`city-page-publish-${p.city_slug}`}
                  />
                  <span className={`text-[10px] uppercase tracking-wider font-mono ${p.is_published ? 'text-green-700' : 'text-[#1F2A4A]/50'}`}>
                    {p.is_published ? 'Publiée' : 'Brouillon'}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {p.is_published && (
                    <a
                      href={`/fr/sellier/${p.city_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#1F2A4A]/55 hover:text-[#C9B896] transition-colors p-1.5"
                      title="Ouvrir la page publique"
                      data-testid={`city-page-open-${p.city_slug}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                  <button
                    onClick={() => setEditing(p)}
                    className="text-[#1F2A4A]/55 hover:text-[#C9B896] transition-colors p-1.5"
                    data-testid={`city-page-edit-${p.city_slug}`}
                    title="Modifier"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(p)}
                    className="text-[#1F2A4A]/55 hover:text-red-600 transition-colors p-1.5"
                    data-testid={`city-page-delete-${p.city_slug}`}
                    title="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <CityPageEditor
          page={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

// Inline edit / create dialog. Body & intro are HTML (admin pastes the
// generated content; future enhancement: rich-text editor).
const CityPageEditor = ({ page, onClose, onSaved }) => {
  const isNew = !!page?._new;
  const [form, setForm] = useState({
    city_name: page.city_name || '',
    distance_km: page.distance_km || 0,
    region: page.region || "Provence-Alpes-Côte d'Azur",
    meta_title: page.meta_title || '',
    meta_description: page.meta_description || '',
    intro_html: page.intro_html || '',
    body_html: page.body_html || '',
    is_published: !!page.is_published,
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.city_name.trim()) {
      toast.error("Renseigne le nom de la ville");
      return;
    }
    setBusy(true);
    try {
      if (isNew) {
        await api.adminCreateCityPage(form);
        toast.success("Page créée");
      } else {
        await api.adminUpdateCityPage(page.id, form);
        toast.success("Page mise à jour");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[88vh] overflow-y-auto" data-testid="city-page-editor">
        <DialogHeader>
          <DialogTitle className="text-[#1F2A4A]">
            {isNew ? "Nouvelle page locale" : `Modifier — ${page.city_name}`}
          </DialogTitle>
          <DialogDescription>
            Le contenu HTML stocké ici est servi tel quel aux visiteurs. Garde un ton qualitatif et inclus le nom de la ville plusieurs fois pour le SEO.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[#1F2A4A]">Ville</Label>
              <Input value={form.city_name} onChange={(e) => setForm({ ...form, city_name: e.target.value })} className="text-[#1F2A4A] placeholder:text-[#1F2A4A]/40" data-testid="city-page-name" />
            </div>
            <div>
              <Label className="text-[#1F2A4A]">Distance (km)</Label>
              <Input type="number" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: Number(e.target.value) || 0 })} className="text-[#1F2A4A]" data-testid="city-page-distance" />
            </div>
            <div>
              <Label className="text-[#1F2A4A]">Région</Label>
              <Input value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="text-[#1F2A4A]" data-testid="city-page-region" />
            </div>
          </div>

          <div>
            <Label className="text-[#1F2A4A]">Meta title (SEO)</Label>
            <Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} placeholder="≤ 60 caractères" className="text-[#1F2A4A] placeholder:text-[#1F2A4A]/40" data-testid="city-page-meta-title" />
          </div>
          <div>
            <Label className="text-[#1F2A4A]">Meta description (SEO)</Label>
            <Textarea rows={2} value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} placeholder="≤ 160 caractères" data-testid="city-page-meta-desc" />
          </div>

          <div>
            <Label className="text-[#1F2A4A]">Introduction (HTML)</Label>
            <Textarea rows={4} value={form.intro_html} onChange={(e) => setForm({ ...form, intro_html: e.target.value })} className="font-mono text-xs" data-testid="city-page-intro" />
          </div>
          <div>
            <Label className="text-[#1F2A4A]">Corps de la page (HTML)</Label>
            <Textarea rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="font-mono text-xs" data-testid="city-page-body" />
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-[#1F2A4A]/10">
            <Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="city-page-published-toggle" />
            <span className="text-sm text-[#1F2A4A]">
              {form.is_published ? <Badge className="bg-green-100 text-green-700">Publiée — accessible publiquement</Badge> : <Badge variant="outline">Brouillon — invisible</Badge>}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-[#1F2A4A]/10">
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166]" data-testid="city-page-save-btn">
            {busy ? 'Enregistrement…' : (isNew ? 'Créer' : 'Enregistrer')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CityLandingPagesAdmin;
